import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import { useT } from "../i18n";
import type { MatchProfile } from "./types";

const PLAY_MS = 3400;
const REDUCED_MS = 400;
/** The single token travels, then touches the card. */
const TOUCH_MS = 900;
const BURST_COUNT = 100;

function prefersReduced(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Stable pseudo-random from an index — the burst looks scattered but never
 *  re-rolls between renders, so a remount does not jump the particles. */
function rand(i: number, salt: number): number {
  const x = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

type Particle = { dx: number; dy: number; delay: number; scale: number; rot: number };

function buildBurst(kind: "heart" | "flame"): Particle[] {
  return Array.from({ length: BURST_COUNT }, (_, i) => {
    const a = rand(i, 1) * Math.PI * 2;
    const reach = 40 + rand(i, 2) * 60;
    // Hearts bloom outward and drift up. Flames are thrown off the top of the
    // screen — Masco: "disappear into the nothingness of the end of the screen".
    const lift = kind === "flame" ? 120 + rand(i, 3) * 90 : 30 + rand(i, 3) * 40;
    return {
      dx: Math.cos(a) * reach,
      dy: Math.sin(a) * reach * 0.55 - lift,
      delay: rand(i, 4) * 420,
      scale: 0.55 + rand(i, 5) * 0.95,
      rot: (rand(i, 6) - 0.5) * 80,
    };
  });
}

/**
 * The send animation for the heart and the flame (rewritten 2026-09-21).
 *
 * Masco: "Make it that the card stays visible, not that the picture
 * disappears. Let one heart emoji touch the profile card and when it does, a
 * hundred hearts pop up and fade away. Same with the flame."
 *
 * So: the portrait never melts and never leaves. One emoji flies in, lands on
 * the card, and on contact a hundred of them burst out of that point.
 */
export function FlameSend({
  profile,
  kind = "flame",
  onComplete,
  settled = false,
}: {
  profile: MatchProfile;
  kind?: "heart" | "flame";
  onComplete: () => void;
  /** Already finished — keep the last frame, do not restart the timer. */
  settled?: boolean;
}) {
  const t = useT();
  const root = useRef<HTMLDivElement>(null);
  const done = useRef(settled);
  const timer = useRef(0);
  const [reduced] = useState(prefersReduced);
  const [touched, setTouched] = useState(settled);
  const burst = useMemo(() => buildBurst(kind), [kind]);
  const glyph = kind === "heart" ? "❤️" : "🔥";

  const finish = () => {
    if (done.current) return;
    done.current = true;
    window.clearTimeout(timer.current);
    onComplete();
  };

  useEffect(() => {
    if (settled) {
      done.current = true;
      setTouched(true);
      return;
    }
    done.current = false;
    setTouched(reduced);
    root.current?.focus();
    const hit = reduced
      ? 0
      : window.setTimeout(() => setTouched(true), TOUCH_MS);
    timer.current = window.setTimeout(finish, reduced ? REDUCED_MS : PLAY_MS);
    return () => {
      window.clearTimeout(timer.current);
      if (hit) window.clearTimeout(hit);
    };
    // finish is stable for this mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id, reduced, settled]);

  return (
    <div
      className={`match-flame-send${reduced ? " is-reduced" : ""}${
        touched ? " is-touched" : ""
      }`}
      data-kind={kind}
      role="dialog"
      aria-modal="true"
      aria-labelledby="match-flame-send-title"
      onClick={finish}
      onKeyDown={(e) => {
        if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          finish();
        }
      }}
      tabIndex={0}
      ref={root}
    >
      <div className="match-flame-send__veil" aria-hidden />
      <div className="match-flame-send__stage" aria-hidden>
        <div className="match-flame-send__bloom" />
        {/* The card. It stays, whole, for the entire animation. */}
        <div className="match-flame-send__face">
          <span>{profile.firstName.charAt(0)}</span>
          {profile.photoUrl ? (
            <img
              src={profile.photoUrl}
              alt=""
              decoding="async"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
        </div>
        {/* One emoji, travelling in to touch it. */}
        <div className="match-flame-send__token">
          <span className="match-flame-send__glyph">{glyph}</span>
        </div>
        {/* A hundred, out of the point of contact. */}
        {touched ? (
          <div className="match-flame-send__burst">
            {burst.map((p, i) => (
              <i
                key={i}
                className="match-flame-send__bit"
                style={
                  {
                    "--dx": `${p.dx}px`,
                    "--dy": `${p.dy}px`,
                    "--d": `${p.delay}ms`,
                    "--sc": p.scale,
                    "--rot": `${p.rot}deg`,
                  } as CSSProperties
                }
              >
                {glyph}
              </i>
            ))}
          </div>
        ) : null}
      </div>
      <div className="match-flame-send__copy">
        <p id="match-flame-send-title" className="match-flame-send__title">
          {kind === "heart"
            ? t("match.heartBurstTitle", { name: profile.firstName })
            : t("match.flameBurstTitle", { name: profile.firstName })}
        </p>
        <p className="match-flame-send__hint">{t("match.packSkip")}</p>
      </div>
    </div>
  );
}
